// WhatsApp inbox.
//
// Two panes: the conversations, and the one you are in. Deliberately NOT a
// ticket view — a WhatsApp thread is a running conversation with one person,
// not a work item, and it is keyed by their phone number.
//
// The screen is built around the one rule that governs WhatsApp: Meta only
// allows a free-form reply inside the 24-hour window a customer opens by
// writing in. That window is shown on every row and enforced on the composer,
// because a reply typed after it closes fails at Meta with a confusing error
// rather than anything the agent did wrong.

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import {
  AlertTriangle,
  Download,
  MessageCircle,
  Paperclip,
  Plus,
  RefreshCw,
  Send,
} from 'lucide-react';
import { useToast } from '@centraoneplus/ui';
import { useInboxRealtime } from '@/hooks/useInboxRealtime';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import { fileService } from '@/services/file.service';
import TemplateSendDialog from './TemplateSendDialog';
import {
  whatsappService,
  type WhatsAppCannedReply,
  type WhatsAppConversation,
  type WhatsAppConversationThread,
  type WhatsAppTimelineMessage,
} from '@/services/social-channels.service';

/** The floating shell: cards sit ON the canvas, they do not butt against it. */
const CARD: React.CSSProperties = { borderRadius: 10, boxShadow: 'var(--co-shadow-card)' };
const CARD_CLASS = 'border border-[var(--co-line)] bg-[var(--co-raised)]';

const CONTROL =
  'flex h-[34px] flex-none items-center gap-[7px] whitespace-nowrap rounded-[10px] border ' +
  'border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] ' +
  'text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] ' +
  'disabled:cursor-not-allowed disabled:opacity-50';
const PRIMARY =
  'flex h-[34px] flex-none items-center gap-[7px] rounded-lg bg-[var(--co-brass-ink)] px-[14px] ' +
  'text-[12.5px] font-bold text-[var(--co-brass-on)] hover:opacity-90 ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

// The socket accelerates this; it never replaces it. A connected socket does
// not prove events are flowing (the BFF relay is optional and disables itself
// without a Kafka broker), so the poll stays at one fixed, safe cadence.
const POLL_MS = 15_000;

/** +91 98765 43210 from 919876543210 — grouped, not reformatted per country. */
function prettyPhone(digits: string): string {
  if (!digits) return '—';
  return `+${digits}`;
}

function clock(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function dayLabel(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  return isToday ? clock(iso) : d.toLocaleDateString([], { day: '2-digit', month: 'short' });
}

/** Minutes left on Meta's 24-hour free-form window, or null once it has closed. */
function windowRemaining(expiresAt: string | null): string | null {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const hours = Math.floor(ms / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  return hours > 0 ? `${hours}h ${minutes}m left` : `${minutes}m left`;
}

/** Initials for the avatar: a name if we have one, else the last two digits. */
function initials(name: string | null, phone: string): string {
  const source = (name || '').trim();
  if (source) {
    const parts = source.split(/\s+/).slice(0, 2);
    return parts.map((part) => part[0]).join('').toUpperCase();
  }
  return phone.slice(-2);
}

function sameDay(a: string, b: string): boolean {
  const x = new Date(a);
  const y = new Date(b);
  return (
    x.getFullYear() === y.getFullYear() &&
    x.getMonth() === y.getMonth() &&
    x.getDate() === y.getDate()
  );
}

/** "Today" / "Yesterday" / a date, for the separators between days. */
function daySeparatorLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { day: 'numeric', month: 'long', year: 'numeric' });
}

const STATUS_LABEL: Record<string, string> = {
  pending: 'Sending…',
  sent: 'Sent',
  delivered: 'Delivered',
  read: 'Read',
  failed: 'Failed',
};

export default function WhatsAppInboxPage() {
  const toast = useToast();

  const [conversations, setConversations] = useState<WhatsAppConversation[]>([]);
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [thread, setThread] = useState<WhatsAppConversationThread | null>(null);
  const [canned, setCanned] = useState<WhatsAppCannedReply[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showCanned, setShowCanned] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  /** null = closed; '' = new conversation; a phone = reopen that thread. */
  const [templateTarget, setTemplateTarget] = useState<string | null>(null);

  const [isAttaching, setIsAttaching] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const cannedRef = useRef<HTMLDivElement>(null);

  const loadConversations = useCallback(async () => {
    try {
      const rows = await whatsappService.listConversations();
      setConversations(rows);
      setError(null);
      return rows;
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          'Could not load WhatsApp conversations. Check the channel is configured in Settings.',
      );
      return [];
    }
  }, []);

  // First paint: conversations + the canned replies the composer offers.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      const [rows] = await Promise.all([
        loadConversations(),
        whatsappService
          .getAutoReply()
          .then((settings) => {
            if (!cancelled) setCanned(settings.cannedReplies);
          })
          .catch(() => {
            // Canned replies are a convenience; the inbox works without them.
          }),
      ]);
      if (!cancelled) {
        setIsLoading(false);
        if (rows.length > 0) setSelectedPhone((current) => current ?? rows[0].phone);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadConversations]);

  const loadThread = useCallback(
    async (phone: string, { markRead = false } = {}) => {
      setIsLoadingThread(true);
      try {
        const data = await whatsappService.getConversation(phone);
        setThread(data);
        if (markRead) {
          // Blue ticks for the customer, and clears the unread badge here.
          // Best effort: a failed receipt must not block reading the thread.
          whatsappService
            .markConversationRead(phone)
            .then(() => loadConversations())
            .catch(() => undefined);
        }
      } catch (err: any) {
        toast.error(
          'Could not open the conversation',
          err.response?.data?.message || 'Please try again.',
        );
      } finally {
        setIsLoadingThread(false);
      }
    },
    [toast, loadConversations],
  );

  useEffect(() => {
    if (selectedPhone) void loadThread(selectedPhone, { markRead: true });
  }, [selectedPhone, loadThread]);

  // A customer's message reaches us by webhook; this is how it reaches the
  // screen without waiting out a poll. Refetch rather than render from the
  // event: it is broadcast to the whole tenant room and carries no content.
  const selectedPhoneRef = useRef(selectedPhone);
  selectedPhoneRef.current = selectedPhone;

  const handleInboundMessage = useCallback(() => {
    void loadConversations();
    const open = selectedPhoneRef.current;
    if (open) void loadThread(open);
  }, [loadConversations, loadThread]);

  const { isLive } = useInboxRealtime({
    channel: 'whatsapp',
    onMessage: handleInboundMessage,
  });

  // The list still polls, slowly while the socket is live.
  useEffect(() => {
    const timer = setInterval(() => {
      void loadConversations();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [loadConversations]);

  // So does the open thread.
  useEffect(() => {
    if (!selectedPhone) return undefined;
    const timer = setInterval(() => {
      void loadThread(selectedPhone);
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [selectedPhone, loadThread]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread?.messages.length]);

  // Close the canned-reply popover on an outside click.
  useEffect(() => {
    if (!showCanned) return undefined;
    const onClick = (event: MouseEvent) => {
      if (cannedRef.current && !cannedRef.current.contains(event.target as Node)) {
        setShowCanned(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showCanned]);

  const selected = useMemo(
    () => conversations.find((c) => c.phone === selectedPhone) || null,
    [conversations, selectedPhone],
  );

  const windowOpen = thread?.isWithin24h ?? selected?.isWithin24h ?? false;
  const remaining = windowRemaining(thread?.windowExpiresAt ?? selected?.windowExpiresAt ?? null);

  const handleSend = async () => {
    const message = draft.trim();
    if (!message || !selectedPhone || isSending) return;

    setIsSending(true);
    try {
      const result = await whatsappService.sendTextMessage({ to: selectedPhone, message });
      if (!result.success) {
        // A Meta-side rejection comes back as success:false, not an HTTP error —
        // the outbound row is still written, so the failure is visible in the
        // thread as well as here.
        toast.error('WhatsApp rejected the message', result.message);
      } else {
        setDraft('');
      }
      await Promise.all([loadThread(selectedPhone), loadConversations()]);
    } catch (err: any) {
      toast.error('Could not send', err.response?.data?.message || 'Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  /**
   * Attachments go to the file-service first - the same upload ticket
   * attachments use - and only the resulting id is sent on. The bytes never
   * pass through the BFF, which keeps multipart out of that pipeline.
   */
  const handleAttach = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file || !selectedPhone || isAttaching) return;

    setIsAttaching(true);
    try {
      const [uploaded] = await fileService.uploadFiles([file], {
        entityType: 'WHATSAPP',
        // The conversation's session id — a real UUID. A phone number here is
        // rejected by the file-service, whose entityId column is a uuid.
        // Omitted entirely when the thread has not loaded, since it is optional.
        ...(thread?.id ? { entityId: thread.id } : {}),
      });
      if (!uploaded?.id) {
        toast.error('Upload failed', 'The file could not be stored.');
        return;
      }

      const result = await whatsappService.sendMediaMessage({
        to: selectedPhone,
        fileId: uploaded.id,
        // Whatever is typed in the composer rides along as the caption.
        caption: draft.trim() || undefined,
        filename: file.name,
        mimeType: file.type,
      });

      if (!result.success) {
        toast.error('WhatsApp rejected the attachment', result.message);
      } else {
        setDraft('');
      }
      await Promise.all([loadThread(selectedPhone), loadConversations()]);
    } catch (err: any) {
      toast.error('Could not send', err.response?.data?.message || 'Please try again.');
    } finally {
      setIsAttaching(false);
      // Let the same file be picked again after a failure.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const totalUnread = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

  return (
    <div className="flex h-full min-h-0 flex-col gap-[14px] overflow-hidden bg-[var(--co-canvas)] py-4 pr-4">
      {/* ---------- header card ---------- */}
      <header
        className={clsx(CARD_CLASS, 'flex-none px-5 py-[18px]')}
        style={{ ...CARD, borderRadius: 14 }}
      >
        <div className="flex items-center justify-between gap-[10px]">
          <div className="min-w-0">
            <h1 className="text-[28px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
              WhatsApp
            </h1>
            <p className="co-mono mt-[6px] flex items-center gap-[7px] text-[10.5px] font-medium uppercase tracking-[.13em] text-[var(--co-ink-3)]">
              <span>
                {conversations.length} conversation{conversations.length === 1 ? '' : 's'}
                {totalUnread > 0 ? ` · ${totalUnread} unread` : ''}
              </span>
              {/* Whether new messages are being pushed or merely polled for.
                  Worth showing: it is the difference between a reply appearing
                  at once and up to a minute later. */}
              <span
                className="flex items-center gap-[4px]"
                title={
                  isLive
                    ? 'Connected - new messages appear as they arrive'
                    : 'Not connected - checking for new messages periodically'
                }
              >
                <span
                  aria-hidden
                  className={clsx(
                    'h-[6px] w-[6px] rounded-full',
                    isLive ? 'bg-[var(--co-green)]' : 'bg-[var(--co-ink-4)]',
                  )}
                />
                {isLive ? 'Live' : 'Polling'}
              </span>
            </p>
          </div>
          <div className="flex h-[34px] flex-none items-center gap-2">
            <button
              type="button"
              className={CONTROL}
              onClick={() => setTemplateTarget('')}
            >
              <Plus className="h-[13px] w-[13px]" />
              New conversation
            </button>
            <button
              type="button"
              className={CONTROL}
              onClick={() => {
                void loadConversations();
                if (selectedPhone) void loadThread(selectedPhone);
              }}
            >
              <RefreshCw className="h-[13px] w-[13px]" />
              Refresh
            </button>
            <PageHeaderMeta />
          </div>
        </div>
      </header>

      {error && (
        <div
          className={clsx(CARD_CLASS, 'flex flex-none items-center gap-2 px-4 py-3')}
          style={{ ...CARD, borderColor: 'var(--co-terracotta)' }}
        >
          <AlertTriangle className="h-4 w-4 flex-none text-[var(--co-terracotta)]" />
          <span className="text-[12.5px] text-[var(--co-ink-2)]">{error}</span>
        </div>
      )}

      {/* ---------- two panes ---------- */}
      <div className="flex min-h-0 flex-1 gap-[14px]">
        {/* conversation list */}
        <aside
          className={clsx(CARD_CLASS, 'flex w-[320px] flex-none flex-col overflow-hidden')}
          style={CARD}
        >
          <div className="flex-none border-b border-[var(--co-line)] px-4 py-3">
            <span className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">
              Conversations
            </span>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {isLoading && (
              <p className="px-4 py-6 text-[12.5px] text-[var(--co-ink-3)]">Loading…</p>
            )}

            {!isLoading && conversations.length === 0 && (
              <div className="px-4 py-8 text-center">
                <MessageCircle className="mx-auto h-6 w-6 text-[var(--co-ink-3)]" />
                <p className="mt-3 text-[12.5px] leading-[1.55] text-[var(--co-ink-3)]">
                  No conversations yet. They appear here the moment someone messages your
                  WhatsApp number.
                </p>
              </div>
            )}

            {conversations.map((conversation) => {
              const isSelected = conversation.phone === selectedPhone;
              return (
                <button
                  key={conversation.id}
                  type="button"
                  onClick={() => setSelectedPhone(conversation.phone)}
                  className="relative flex w-full items-start gap-3 px-4 py-[13px] text-left transition-colors"
                  style={{
                    background: isSelected ? 'var(--co-selected)' : 'transparent',
                    boxShadow: 'inset 0 -1px 0 var(--co-line-subtle)',
                  }}
                >
                  {/* The selected marker is a rail, not a fill: it survives
                      hover and reads at a glance down a long list. */}
                  {isSelected && (
                    <span
                      className="absolute inset-y-0 left-0 w-[3px]"
                      style={{ background: 'var(--co-brass-ink)' }}
                      aria-hidden
                    />
                  )}

                  <span
                    className="mt-[1px] flex h-9 w-9 flex-none items-center justify-center rounded-full text-[12px] font-bold"
                    style={{
                      background: 'var(--co-brass-wash)',
                      color: 'var(--co-brass-ink)',
                    }}
                    aria-hidden
                  >
                    {initials(conversation.name, conversation.phone)}
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-[13px] font-semibold text-[var(--co-ink)]">
                        {conversation.name || prettyPhone(conversation.phone)}
                      </span>
                      <span className="co-mono flex-none text-[10px] text-[var(--co-ink-3)]">
                        {dayLabel(conversation.lastMessageAt)}
                      </span>
                    </span>

                    <span className="mt-[3px] flex items-center justify-between gap-2">
                      <span
                        className="truncate text-[12px]"
                        style={{
                          color:
                            conversation.unreadCount > 0
                              ? 'var(--co-ink-2)'
                              : 'var(--co-ink-3)',
                          fontWeight: conversation.unreadCount > 0 ? 600 : 400,
                        }}
                      >
                        {conversation.lastDirection === 'outbound' ? 'You: ' : ''}
                        {conversation.lastMessage || 'No text content'}
                      </span>
                      {conversation.unreadCount > 0 && (
                        <span
                          className="flex-none rounded-full px-[6px] py-[1px] text-[10px] font-bold leading-[1.5]"
                          style={{
                            background: 'var(--co-brass-ink)',
                            color: 'var(--co-brass-on)',
                          }}
                        >
                          {conversation.unreadCount}
                        </span>
                      )}
                    </span>

                    {!conversation.isWithin24h && (
                      <span className="co-mono mt-[5px] inline-block text-[9px] uppercase tracking-[.14em] text-[var(--co-ink-3)]">
                        Window closed
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* thread */}
        <section
          className={clsx(CARD_CLASS, 'flex min-w-0 flex-1 flex-col overflow-hidden')}
          style={CARD}
        >
          {!selectedPhone && (
            <div className="flex flex-1 items-center justify-center">
              <p className="text-[12.5px] text-[var(--co-ink-3)]">
                Pick a conversation to read it.
              </p>
            </div>
          )}

          {selectedPhone && (
            <>
              <div className="flex flex-none items-center justify-between gap-3 border-b border-[var(--co-line)] px-5 py-3">
                <div className="min-w-0">
                  <p className="truncate text-[15px] font-bold text-[var(--co-ink)]">
                    {thread?.name || selected?.name || prettyPhone(selectedPhone)}
                  </p>
                  <p className="co-mono mt-[3px] text-[10px] uppercase tracking-[.14em] text-[var(--co-ink-3)]">
                    {prettyPhone(selectedPhone)}
                    {remaining ? ` · ${remaining}` : ''}
                  </p>
                </div>
              </div>

              {/* transcript */}
              <div
                className="min-h-0 flex-1 overflow-y-auto px-5 py-4"
                style={{ background: 'var(--co-sunken)' }}
              >
                {isLoadingThread && !thread && (
                  <p className="text-[12.5px] text-[var(--co-ink-3)]">Loading…</p>
                )}

                {thread?.messages.map((message, index) => {
                  const previous = thread.messages[index - 1];
                  const newDay = !previous || !sameDay(previous.at, message.at);
                  // A run from the same side sits tighter, so a back-and-forth
                  // reads as a conversation rather than a list of boxes.
                  const grouped =
                    !newDay && previous?.direction === message.direction;

                  return (
                    <Fragment key={message.id}>
                      {newDay && (
                        <div className="my-4 flex justify-center">
                          <span
                            className="co-mono rounded-full px-[10px] py-[3px] text-[9.5px] uppercase tracking-[.14em]"
                            style={{
                              background: 'var(--co-raised)',
                              color: 'var(--co-ink-3)',
                              border: '1px solid var(--co-line-subtle)',
                            }}
                          >
                            {daySeparatorLabel(message.at)}
                          </span>
                        </div>
                      )}
                      <MessageBubble message={message} grouped={grouped} />
                    </Fragment>
                  );
                })}

                <div ref={endRef} />
              </div>

              {/* composer */}
              <div className="flex-none border-t border-[var(--co-line)] px-5 py-3">
                {!windowOpen && (
                  <div className="mb-2 flex items-start gap-2 rounded-lg border border-[var(--co-line)] px-3 py-2">
                    <AlertTriangle className="mt-[2px] h-[13px] w-[13px] flex-none text-[var(--co-terracotta)]" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[11.5px] leading-[1.5] text-[var(--co-ink-3)]">
                        The 24-hour reply window has closed. WhatsApp only allows a free-form
                        message within 24 hours of the customer's last message — send an
                        approved template to reopen the conversation, or wait for them to write
                        again.
                      </p>
                      <button
                        type="button"
                        className={clsx(CONTROL, 'mt-2')}
                        onClick={() => setTemplateTarget(selectedPhone)}
                      >
                        <Send className="h-[13px] w-[13px]" />
                        Send template
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex items-end gap-2">
                  <textarea
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        void handleSend();
                      }
                    }}
                    rows={2}
                    disabled={!windowOpen}
                    placeholder={
                      windowOpen ? 'Write a reply…' : 'Replies are closed until they write again'
                    }
                    className="min-h-[46px] flex-1 resize-none rounded-lg border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 py-2 text-[12.5px] text-[var(--co-ink)] placeholder:text-[var(--co-ink-3)] focus:border-[var(--co-brass)] focus:outline-none disabled:opacity-60"
                  />

                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={(event) => void handleAttach(event.target.files)}
                  />
                  <button
                    type="button"
                    className={CONTROL}
                    disabled={!windowOpen || isAttaching}
                    onClick={() => fileInputRef.current?.click()}
                    title="Attach an image, document, video or audio file"
                  >
                    <Paperclip className="h-[13px] w-[13px]" />
                    {isAttaching ? 'Sending…' : 'Attach'}
                  </button>

                  <div className="relative flex-none" ref={cannedRef}>
                    <button
                      type="button"
                      className={CONTROL}
                      disabled={!windowOpen || canned.length === 0}
                      onClick={() => setShowCanned((open) => !open)}
                      title={
                        canned.length === 0
                          ? 'Add canned replies in Settings → Social Channels'
                          : 'Insert a canned reply'
                      }
                    >
                      Canned
                    </button>

                    {showCanned && canned.length > 0 && (
                      <div
                        className={clsx(CARD_CLASS, 'absolute bottom-[42px] right-0 w-[280px] p-1')}
                        style={{ borderRadius: 10, boxShadow: 'var(--co-shadow-pop)' }}
                      >
                        {canned.map((reply) => (
                          <button
                            key={reply.id}
                            type="button"
                            onClick={() => {
                              setDraft(reply.body);
                              setShowCanned(false);
                            }}
                            className="block w-full rounded-lg px-3 py-2 text-left hover:bg-[var(--co-brass-wash)]"
                          >
                            <span className="block text-[12.5px] font-semibold text-[var(--co-ink)]">
                              {reply.title}
                            </span>
                            <span className="mt-[2px] block truncate text-[11.5px] text-[var(--co-ink-3)]">
                              {reply.body}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className={PRIMARY}
                    disabled={!windowOpen || !draft.trim() || isSending}
                    onClick={() => void handleSend()}
                  >
                    <Send className="h-[13px] w-[13px]" />
                    {isSending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      {templateTarget !== null && (
        <TemplateSendDialog
          phone={templateTarget || undefined}
          onClose={() => setTemplateTarget(null)}
          onSent={(sentTo) => {
            setTemplateTarget(null);
            // A template opens (or reopens) the conversation, so land in it.
            setSelectedPhone(sentTo);
            void loadConversations();
            void loadThread(sentTo);
          }}
        />
      )}
    </div>
  );
}

const MEDIA_TYPES = new Set(['image', 'video', 'audio', 'document', 'sticker']);

/**
 * Open an attachment in a new tab, from whichever side holds it: our own
 * file-service copy for outbound, Meta's via the proxy for inbound.
 */
async function openAttachment(message: WhatsAppTimelineMessage): Promise<void> {
  if (message.fileId) {
    fileService.downloadFile(message.fileId);
    return;
  }
  if (!message.mediaId) return;

  const url = await whatsappService.getInboundMediaUrl(message.mediaId);
  window.open(url, '_blank', 'noopener');
  // The tab holds its own reference; release ours once it has loaded.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Renders an attachment.
 *
 * Only OUTBOUND media has a `fileId` - that is our own stored copy. Inbound
 * media lives at Meta behind an authenticated, short-lived URL, so for now it
 * is named rather than shown; fetching those bytes is a separate piece of work.
 *
 * The preview URL is presigned and expires, so it is fetched lazily per
 * message rather than held in the timeline payload.
 */
function MediaAttachment({ message }: { message: WhatsAppTimelineMessage }) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const isImage = message.type === 'image' || message.type === 'sticker';
  const label = message.filename || `${message.type} attachment`;

  useEffect(() => {
    if (!isImage) return undefined;
    let cancelled = false;
    // Object URLs are only created for inbound media, and must be revoked.
    let objectUrl: string | null = null;

    const load = async () => {
      try {
        if (message.fileId) {
          // Outbound: our own stored copy, reachable by presigned URL.
          const res = await fileService.getPresignedUrl(message.fileId);
          if (!cancelled) setPreviewUrl(res.url);
        } else if (message.mediaId) {
          // Inbound: lives at Meta behind an authenticated URL, so it is
          // proxied and handed back as a blob.
          objectUrl = await whatsappService.getInboundMediaUrl(message.mediaId);
          if (cancelled) {
            URL.revokeObjectURL(objectUrl);
            objectUrl = null;
          } else {
            setPreviewUrl(objectUrl);
          }
        }
      } catch {
        // Fall back to the named chip below.
      }
    };
    void load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [message.fileId, message.mediaId, isImage]);

  if (previewUrl) {
    return (
      <a href={previewUrl} target="_blank" rel="noreferrer" className="block">
        <img
          src={previewUrl}
          alt={label}
          className="mb-1 max-h-[220px] w-auto rounded-[8px] border border-[var(--co-line)]"
        />
      </a>
    );
  }

  return (
    <div className="mb-1 flex items-center gap-2 rounded-[8px] border border-[var(--co-line)] px-[9px] py-[7px]">
      <Paperclip className="h-[13px] w-[13px] flex-none text-[var(--co-ink-3)]" />
      <span className="min-w-0 flex-1 truncate text-[12px] text-[var(--co-ink)]">{label}</span>
      {(message.fileId || message.mediaId) && (
        <button
          type="button"
          aria-label={`Open ${label}`}
          onClick={() => void openAttachment(message)}
          className="flex-none text-[var(--co-ink-3)] hover:text-[var(--co-ink)]"
        >
          <Download className="h-[13px] w-[13px]" />
        </button>
      )}
    </div>
  );
}

/** One line of the transcript. Inbound left, outbound right, as in WhatsApp. */
function MessageBubble({
  message,
  grouped = false,
}: {
  message: WhatsAppTimelineMessage;
  grouped?: boolean;
}) {
  const isOutbound = message.direction === 'outbound';
  const failed = message.status === 'failed';
  const isMedia = MEDIA_TYPES.has(message.type);

  return (
    <div
      className={clsx('flex', isOutbound ? 'justify-end' : 'justify-start')}
      style={{ marginTop: grouped ? 3 : 10 }}
    >
      <div
        className="max-w-[72%] px-[11px] py-[7px]"
        style={{
          // Ours is filled, theirs is a raised card - the fastest way to read
          // direction without hunting for alignment. The square corner points
          // at its own side, standing in for a tail.
          background: isOutbound ? 'var(--co-brass-ink)' : 'var(--co-raised)',
          color: isOutbound ? 'var(--co-brass-on)' : 'var(--co-ink)',
          border: isOutbound ? 'none' : '1px solid var(--co-line-subtle)',
          borderRadius: isOutbound ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
          boxShadow: isOutbound ? 'none' : 'var(--co-shadow-card)',
          ...(failed ? { border: '1px solid var(--co-terracotta)' } : {}),
        }}
      >
        {isMedia && <MediaAttachment message={message} />}

        {message.text ? (
          <p className="whitespace-pre-wrap break-words text-[12.5px] leading-[1.5]">
            {message.text}
          </p>
        ) : (
          !isMedia && (
            // Location and contact cards are stored but not rendered yet —
            // say what arrived rather than showing an empty bubble.
            <p className="text-[12.5px] italic text-[var(--co-ink-3)]">
              [{message.type} message]
            </p>
          )
        )}

        {/* Inherits the bubble's text colour so it stays legible on both the
            filled and the raised variant, in light and dark alike. */}
        <div
          className="mt-[3px] flex items-center justify-end gap-[6px] text-[9.5px] opacity-65"
          style={failed ? { color: 'var(--co-terracotta)', opacity: 1 } : undefined}
        >
          {message.automated && (
            <span className="co-mono uppercase tracking-[.14em]">Auto</span>
          )}
          <span className="co-mono">{clock(message.at)}</span>
          {isOutbound && message.status && (
            <span className="co-mono">{STATUS_LABEL[message.status] || message.status}</span>
          )}
        </div>

        {failed && message.errorMessage && (
          <p
            className="mt-[5px] rounded-[6px] px-[7px] py-[4px] text-[11px] leading-[1.4]"
            style={{
              background: 'var(--co-terracotta-wash)',
              color: 'var(--co-terracotta-ink)',
            }}
          >
            {message.errorMessage}
          </p>
        )}
      </div>
    </div>
  );
}
